import { useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import { removeViewportMeta } from '#utils/viewport';
import type { PanelItem } from '#addons/types';
import Header from './Header';
import Footer from './Footer';
import AdminPanel from './AdminPanel';

const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COMPACT = 72;

interface AdminLayoutProps {
  panel: PanelItem[];
}

export default function AdminLayout(props: AdminLayoutProps) {
  const { panel } = props;
  const [menuFullWitdh, setMenuFullWitdh] = useState(true);
  const location = useLocation();

  const width = menuFullWitdh ? SIDEBAR_EXPANDED : SIDEBAR_COMPACT;
  const toggleTitle = menuFullWitdh ? 'Collapse menu' : 'Expand menu';

  useLayoutEffect(() => {
    const isPhoneViewport = window.matchMedia('(max-width:600px)').matches;
    const removedViewportMeta = removeViewportMeta();
    if (removedViewportMeta && isPhoneViewport) {
      window.location.reload();
    }
  }, [location.pathname, location.search]);

  return (
    <Container maxWidth={false}>
      <CssBaseline />
      <Header />
      <Box sx={{ display: 'flex', alignItems: 'stretch', '@media print': { display: 'block' } }}>
        <Box
          component="aside"
          sx={(theme) => ({
            width,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            backdropFilter: 'blur(6px)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: 220,
            }),
            overflow: 'hidden',
            '@media print': { display: 'none' },
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: menuFullWitdh ? 'space-between' : 'center',
              px: menuFullWitdh ? 2 : 0,
              py: 1.5,
              minHeight: 48,
            }}
          >
            {menuFullWitdh && (
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                }}
              >
                Management
              </Typography>
            )}
            <Tooltip title={toggleTitle} placement="right" arrow>
              <IconButton
                id="admin-menu-handler"
                size="small"
                onClick={() => setMenuFullWitdh((v) => !v)}
                sx={(theme) => ({
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                })}
              >
                {menuFullWitdh ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ opacity: 0.6 }} />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.15),
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.3),
              },
            }}
          >
            <AdminPanel
              panel={panel}
              compact={!menuFullWitdh}
              onExpandSidebar={() => setMenuFullWitdh(true)}
            />
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            pt: 5,
            pl: 3,
            pr: 1,
            '& .MuiTextField-root': { mb: 1 },
            '&>.MuiBox-root': { pb: 2 },
            '@media print': { maxWidth: 'initial', pl: 0 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <Footer />
    </Container>
  );
}
